/**
 * Payment run — `/finance/payment-runs/$paymentRunId`.
 *
 * Submission raises an approval request whose amount is the run total; when no
 * rule requires a step the engine AUTO-APPROVES and the run moves straight to
 * `approved`. The screen therefore branches on the returned `status` rather
 * than assuming an approval is pending — that is why there is no separate
 * "approve" button here: approval is the workflow's decision, not this
 * screen's, and the contract publishes no approve operation on a run.
 *
 * Export is deterministic and its SHA-256 is stored on the first export. A
 * later export producing different bytes is refused rather than handing the
 * treasury a second file for a run the bank may already hold, so the rendered
 * file and its hash are shown for inspection instead of being auto-downloaded.
 */
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button, Card, CardContent, CardHeader, CardTitle } from "@erp/ui";
import { ArrowLeft } from "lucide-react";

import { surfaceProblem } from "@/components/form";
import { MoneyDisplay } from "@/components/money/money-display";
import { useIdempotentMutation } from "@/features/shared/ops-api";

import {
  exportPaymentRunFile,
  postPaymentRun,
  submitPaymentRun,
  type PaymentFile,
  type PaymentFileRequest,
  type PaymentRun,
} from "../api";
import {
  DefinitionList,
  NoticeBanner,
  PageHeader,
  ProblemBanner,
  StatusBadge,
  TextField,
} from "../components/fields";
import { financeKeys, usePartyOptions, usePaymentRun } from "../queries";

const RUN_STATUS_VARIANTS = {
  draft: "outline",
  proposed: "secondary",
  pending_approval: "warning",
  approved: "default",
  posted: "success",
  cancelled: "destructive",
} as const;

export interface PaymentRunPageProps {
  paymentRunId?: string;
  onBack: () => void;
  canSubmit?: boolean;
  canPost?: boolean;
  canExport?: boolean;
  canReadParties?: boolean;
}

export function PaymentRunPage({
  paymentRunId,
  onBack,
  canSubmit = true,
  canPost = true,
  canExport = true,
  canReadParties = true,
}: PaymentRunPageProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const runQuery = usePaymentRun(paymentRunId);
  const partiesQuery = usePartyOptions("supplier", canReadParties);
  const run = runQuery.data;

  const [submissionReason, setSubmissionReason] = useState("");
  const [debtorName, setDebtorName] = useState("");
  const [debtorIban, setDebtorIban] = useState("");
  const [debtorBic, setDebtorBic] = useState("");
  const [file, setFile] = useState<PaymentFile | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const onRunChanged = (next: PaymentRun) => {
    queryClient.setQueryData(financeKeys.paymentRuns.detail(next.id), next);
    void queryClient.invalidateQueries({ queryKey: financeKeys.paymentRuns.all });
  };

  const submitMutation = useIdempotentMutation<
    PaymentRun,
    { paymentRunId: string; reason: string }
  >({
    mutationFn: (input, key) =>
      submitPaymentRun(
        input.paymentRunId,
        input.reason === "" ? {} : { reason: input.reason },
        key,
      ),
    onSuccess: onRunChanged,
    onError: (error) => surfaceProblem(error, t),
  });

  const postMutation = useIdempotentMutation<PaymentRun, { paymentRunId: string }>({
    mutationFn: (input, key) => postPaymentRun(input.paymentRunId, key),
    onSuccess: onRunChanged,
    onError: (error) => surfaceProblem(error, t),
  });

  const exportMutation = useIdempotentMutation<
    PaymentFile,
    { paymentRunId: string; body: PaymentFileRequest }
  >({
    mutationFn: (input, key) => exportPaymentRunFile(input.paymentRunId, input.body, key),
    onSuccess: (exported) => {
      setFile(exported);
      void queryClient.invalidateQueries({ queryKey: financeKeys.paymentRuns.all });
    },
    onError: (error) => surfaceProblem(error, t),
  });

  if (runQuery.isLoading) {
    return <div className="p-4 text-sm text-muted-foreground">{t("app.loading", "Loading…")}</div>;
  }

  if (run === undefined) {
    return (
      <div className="flex flex-col gap-4 p-4">
        <ProblemBanner>
          {t("finance.paymentRuns.notFound", "This payment run could not be loaded.")}
        </ProblemBanner>
        <Button type="button" variant="outline" onClick={onBack}>
          {t("finance.paymentRuns.back", "Back to payment runs")}
        </Button>
      </div>
    );
  }

  const partyName = (id: string): string =>
    (partiesQuery.data ?? []).find((party) => party.id === id)?.display_name ?? id;

  const submitExport = () => {
    if (debtorName.trim() === "" || debtorIban.trim() === "" || debtorBic.trim() === "") {
      setFormError(
        t(
          "finance.paymentRuns.debtorRequired",
          "The payment file needs the debtor account name, IBAN and BIC.",
        ),
      );
      return;
    }
    setFormError(null);
    exportMutation.mutate({
      paymentRunId: run.id,
      body: {
        format: "sepa_pain001",
        debtor_account_name: debtorName.trim(),
        debtor_iban: debtorIban.trim().toUpperCase(),
        debtor_bic: debtorBic.trim().toUpperCase(),
      },
    });
  };

  const submittable = run.status === "draft" || run.status === "proposed";
  const postable = run.status === "approved";

  return (
    <div className="flex flex-col gap-4 p-4">
      <PageHeader
        title={run.run_number}
        description={t("finance.paymentRuns.detailSubtitle", "Payment date {{date}}", {
          date: run.payment_date,
        })}
        actions={
          <>
            <Button type="button" variant="ghost" onClick={onBack}>
              <ArrowLeft className="size-4 rtl:-scale-x-100" aria-hidden="true" />
              {t("finance.paymentRuns.back", "Back to payment runs")}
            </Button>
            {canPost ? (
              <Button
                type="button"
                disabled={!postable}
                loading={postMutation.isPending}
                onClick={() => postMutation.mutate({ paymentRunId: run.id })}
              >
                {t("finance.paymentRuns.post", "Post the run")}
              </Button>
            ) : null}
          </>
        }
      />

      <Card>
        <CardContent className="flex flex-col gap-4 pt-6">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge
              status={run.status}
              label={t(`finance.paymentRuns.status.${run.status}`, run.status)}
              variants={RUN_STATUS_VARIANTS}
            />
            {run.approval_request_id === null ? null : (
              <span className="text-sm text-muted-foreground">
                {t(
                  "finance.paymentRuns.approvalRaised",
                  "An approval request is open for this run.",
                )}
              </span>
            )}
          </div>
          <DefinitionList
            rows={[
              {
                label: t("finance.paymentRuns.paymentDate", "Payment date"),
                value: <span dir="ltr">{run.payment_date}</span>,
              },
              {
                label: t("finance.paymentRuns.dueOnOrBefore", "Due on or before"),
                value: <span dir="ltr">{run.due_on_or_before}</span>,
              },
              {
                label: t("finance.common.currency", "Currency"),
                value: <span dir="ltr">{run.currency}</span>,
              },
              {
                label: t("finance.paymentRuns.total", "Total"),
                value: <MoneyDisplay value={run.total_amount} />,
              },
              {
                label: t("finance.paymentRuns.fileHash", "Exported file hash"),
                value:
                  run.file_hash === "" ? (
                    <span className="text-muted-foreground">
                      {t("finance.paymentRuns.notExported", "Not exported yet")}
                    </span>
                  ) : (
                    <span dir="ltr" className="truncate font-mono text-xs">
                      {run.file_hash}
                    </span>
                  ),
              },
            ]}
          />
          {postable || !canPost ? null : (
            <NoticeBanner>
              {run.status === "posted"
                ? t("finance.paymentRuns.alreadyPosted", "This run has already posted.")
                : t(
                    "finance.paymentRuns.postBlocked",
                    "Posting is refused until the run is approved.",
                  )}
            </NoticeBanner>
          )}
        </CardContent>
      </Card>

      {canSubmit && submittable ? (
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>{t("finance.paymentRuns.submitSection", "Submit for approval")}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-end gap-3">
            <TextField
              id="run-submission-reason"
              label={t("finance.paymentRuns.submissionReason", "Note")}
              value={submissionReason}
              onChange={setSubmissionReason}
              className="min-w-64 flex-1"
              maxLength={200}
            />
            <Button
              type="button"
              loading={submitMutation.isPending}
              onClick={() =>
                submitMutation.mutate({ paymentRunId: run.id, reason: submissionReason.trim() })
              }
            >
              {t("finance.paymentRuns.submit", "Submit run")}
            </Button>
            <p className="w-full text-sm text-muted-foreground">
              {t(
                "finance.paymentRuns.submitHint",
                "When no rule requires a step the engine approves it immediately — check the status afterwards.",
              )}
            </p>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("finance.paymentRuns.linesSection", "Selected invoices")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-muted-foreground">
                <th scope="col" className="p-2 text-start">
                  #
                </th>
                <th scope="col" className="p-2 text-start">
                  {t("finance.paymentRuns.supplier", "Supplier")}
                </th>
                <th scope="col" className="p-2 text-start">
                  {t("finance.paymentRuns.invoice", "Invoice")}
                </th>
                <th scope="col" className="p-2 text-start">
                  {t("finance.paymentRuns.dueDate", "Due")}
                </th>
                <th scope="col" className="p-2 text-end">
                  {t("finance.paymentRuns.gross", "Gross")}
                </th>
                <th scope="col" className="p-2 text-end">
                  {t("finance.paymentRuns.discount", "Discount")}
                </th>
                <th scope="col" className="p-2 text-end">
                  {t("finance.paymentRuns.net", "Net")}
                </th>
              </tr>
            </thead>
            <tbody>
              {run.lines.map((line) => (
                <tr key={line.id} className="border-b">
                  <td className="p-2 tabular-nums">{line.line_number}</td>
                  <td className="p-2">{partyName(line.party_id)}</td>
                  <td dir="ltr" className="p-2 font-mono text-xs">
                    {line.supplier_invoice_number}
                  </td>
                  <td dir="ltr" className="p-2">
                    {line.due_date}
                  </td>
                  <td className="p-2 text-end">
                    <MoneyDisplay value={line.gross_amount} />
                  </td>
                  <td className="p-2 text-end">
                    <MoneyDisplay value={line.discount_amount} />
                  </td>
                  <td className="p-2 text-end">
                    <MoneyDisplay value={line.net_amount} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {canExport ? (
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>{t("finance.paymentRuns.exportSection", "Bank payment file")}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <p className="text-sm text-muted-foreground">
              {t(
                "finance.paymentRuns.exportHint",
                "ISO 20022 pain.001. The writer is deterministic, so regenerating reproduces the file byte for byte.",
              )}
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <TextField
                id="debtor-name"
                label={t("finance.paymentRuns.debtorName", "Debtor account name")}
                value={debtorName}
                onChange={setDebtorName}
                maxLength={70}
              />
              <TextField
                id="debtor-iban"
                label={t("finance.paymentRuns.debtorIban", "Debtor IBAN")}
                value={debtorIban}
                onChange={setDebtorIban}
                ltr
              />
              <TextField
                id="debtor-bic"
                label={t("finance.paymentRuns.debtorBic", "Debtor BIC")}
                value={debtorBic}
                onChange={setDebtorBic}
                ltr
              />
            </div>

            {formError === null ? null : <ProblemBanner>{formError}</ProblemBanner>}

            <div>
              <Button type="button" loading={exportMutation.isPending} onClick={submitExport}>
                {t("finance.paymentRuns.export", "Export payment file")}
              </Button>
            </div>

            {file === null ? null : (
              <div className="flex flex-col gap-2" data-testid="payment-file">
                <p className="text-sm">
                  <span dir="ltr" className="font-mono">
                    {file.filename}
                  </span>{" "}
                  ·{" "}
                  <span dir="ltr" className="font-mono text-xs">
                    {file.sha256}
                  </span>
                </p>
                <pre
                  dir="ltr"
                  className="max-h-64 overflow-auto rounded-md border bg-muted p-2 text-xs"
                >
                  {file.content}
                </pre>
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
